import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../hooks';

import SodReviewTimePicker from './SodReviewTimePicker';

import './SodReviewModal.css';

interface SodReviewModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

type DurationType = '1week' | '3weeks' | '1month' | '3months' | 'custom' | '';

/**
 * Sod Review Modal
 */
function SodReviewModal({
  isOpen,
  setIsOpen,
}: SodReviewModalProps): ReactElement {
  const l = useInvertedLocalize();
  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  const [commentValue, setCommentValue] = useState('');
  const [isCommentCritical, setIsCommentCritical] = useState(false);
  const [isTimePickerCritical, setIsTimePickerCritical] = useState(false);

  const [periodSelection, setPeriodSelection] = useState<number>(1);
  const [durationSelection, setDurationSelection] = useState<DurationType>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const headerIcon = () => (
    <Icon kind='Comment' color='neutral-1000' size='large' />
  );

  // Reset All On Modal Close
  useEffect(() => {
    if (!isOpen) {
      setIsCommentCritical(false);
      setCommentValue('');
      setPeriodSelection(1);
      setDurationSelection('');
      setStartDate(null);
      setEndDate(null);
      setIsTimePickerCritical(false);
    }
  }, [isOpen]);

  const isSelectedForNow = useCallback(() => {
    if (periodSelection === 1) {
      if (durationSelection === 'custom') {
        return endDate !== null;
      }

      return durationSelection !== '';
    }

    return false;
  }, [periodSelection, durationSelection, endDate]);

  const isSelectedForFuture = useCallback(() => {
    if (periodSelection === 2) {
      if (durationSelection === 'custom') {
        return startDate !== null && endDate !== null;
      }

      return startDate !== null && durationSelection !== '';
    }

    return false;
  }, [periodSelection, durationSelection, startDate, endDate]);

  useEffect(() => {
    if (isSelectedForNow() || isSelectedForFuture()) {
      setIsTimePickerCritical(false);
    }
  }, [isSelectedForNow, isSelectedForFuture]);

  useEffect(() => {
    if (commentValue.length > 0) {
      setIsCommentCritical(false);
    }
  }, [commentValue]);

  const MOCK_PREDEFINED_COMMENTS = [
    'Predefined option 1',
    'Predefined option 2',
    'Predefined option 3',
  ];

  const getModalBodyTimePicker = () => (
    <Box className='SodReviewModal-contentBox'>
      <SodReviewTimePicker
        periodSelection={periodSelection}
        setPeriodSelection={setPeriodSelection}
        durationSelection={durationSelection}
        setDurationSelection={setDurationSelection}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
    </Box>
  );

  const getModalBodyComment = () => (
    <Box className='SodReviewModal-contentBox'>
      <InputField
        name='mitigation-submit-comment'
        kind='multiline'
        label={
          <Typography kind='label' htmlFor='mitigation-submit-comment'>
            {l('Comments')}
          </Typography>
        }
        tabIndex={0}
        value={commentValue}
        setValue={setCommentValue}
        predefinedOptions={MOCK_PREDEFINED_COMMENTS}
        predefinedOptionsConfig={{ lineLimit: 1 }}
        placeholder={l('Describe the review activities')}
        isRequired={commentValue.length < 1 || isCommentCritical}
        isCritical={isCommentCritical}
        setIsCritical={setIsCommentCritical}
        CriticalHelperText={
          <InlineMessage
            kind='missingRequiredField'
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        }
      />
    </Box>
  );

  const showAlertBanner = isTimePickerCritical || isCommentCritical;
  const alertDescription = `Required fields (${
    isTimePickerCritical && isCommentCritical ? 2 : 1
  }) are missing.`;

  const getContentBody = (
    <Box className='SodReviewModal-contentBody'>
      <AlertBanner
        kind='high'
        colorTheme='Critical'
        title='Missing'
        description={alertDescription}
        isVisible={showAlertBanner}
        onCancel={() => {}}
        isFloating
        position={{
          type: 'fixed',
          top: '7rem',
          right: '3rem',
          left: '3rem',
        }}
      />
      <Typography kind='h3'>
        {l(
          'When would you like to start the “In Review” period, and for how long?'
        )}
      </Typography>
      {getModalBodyTimePicker()}
      {isTimePickerCritical ? (
        <InlineMessage
          kind='missingRequiredField'
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      ) : null}
      <Typography kind='h3'>
        {l('What are you working on during the review?')}
      </Typography>
      {getModalBodyComment()}
    </Box>
  );

  const handleClose = () => setIsOpen(false);

  const isSubmitButtonEnable = () =>
    commentValue.length > 0 && (isSelectedForNow() || isSelectedForFuture());

  const handleSumbit = () => {
    if (commentValue.length === 0) {
      setIsCommentCritical(true);
    }

    if (!(isSelectedForNow() || isSelectedForFuture())) {
      setIsTimePickerCritical(true);
    }
  };

  const getFooter = (
    <FooterActionBar
      size='large'
      isScrolled={sectionRefIsScrolled}
      className='SodReviewModal-footer'>
      <Box>
        <div className='SodReviewModal-actionButtons'>
          <Button
            type='button'
            kind='ghost'
            size='medium'
            isDisabled={false}
            onClick={handleClose}>
            {l('Cancel')}
          </Button>
          <Button
            className='SodReviewModal-submitButton'
            type='button'
            kind={isSubmitButtonEnable() ? 'filled' : 'outlined'}
            size='medium'
            onClick={handleSumbit}
            isLoading={false}
            loaderText={l('Loading')}>
            {l('Save')}
          </Button>
        </div>
      </Box>
    </FooterActionBar>
  );

  return (
    <ModalIsland
      onClose={handleClose}
      isOpen={isOpen}
      headerIcon={headerIcon()}
      title={l('Move to "In Review"')}
      subtitle={l(
        'Move the violation to the “In Review” status to track its progress.'
      )}
      contentBody={getContentBody}
      FooterActionBarComp={getFooter}
      className='SodReviewModal'
    />
  );
}

export default SodReviewModal;
